/**
 * スラッグを変えるダイアログ（2026-09-28 creator/slug-rename）。親は SlugSettings.tsx。判断は slugRename.ts（テストあり）。
 *
 * 流れ: 新しいスラッグ → 確認する（GET …/slug/check = 使えない理由と候補）→ 確認の画面 → 再認証 → 変更（POST …/slug）。
 *   確認の画面に出すもの（指示書）: 新旧の URL・独自ドメインは変わらない・古いリンクの 2 択（既定の選択なし）・月額と日割り・
 *   次に変えられる日・メールの送信元（固定なら「元の名前のまま（到達率を守るため）」）・配信停止のリンクはそのまま。
 *
 * WHY 2 択に既定値を置かないか（founder 2026-09-28）: 「転送を残す」を既定にすると、料金が知らないうちに掛かる。
 *   「残さない」を既定にすると、うっかり古いリンクが全部切れる。どちらも本人の判断なので、選ぶまで「変更する」を押せない
 *   （送る直前の changeBody でも止める・サーバーも 400 redirect_choice_required）。
 * WHY 確認（check）を挟むか: 使えない理由（使用済み・予約語・変更の間隔（1 日。2026-10-04 までは 30 日））と空いている候補を、パスワードを打たせる前に見せるため。
 *   ここで「使える」でも、送るまでの間に他の人が取ることはある = POST の 409 slug_taken も入力の段に戻して候補を出す。
 * 「Google で確認」から戻ったとき: 親が action（slugChangeAction = スラッグと 2 択）を読み、initial に入れて開き直す。
 *   ここでは確かめ直してから確認の画面を出し、最後の「変更する」は本人が押す（押すと reauth.run が戻ってきた証明を使う）。
 *   お金（転送の料金）と URL が変わる操作なので、戻った直後に自動では送らない。
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { api, type ApiError } from '../api';
import { errorText, formatDateTime, formatNumber, useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { slugSuggestionsFrom } from '../slugSuggestions';
import {
  canConfirmChange,
  changeBody,
  checkSuggestions,
  isDailyProration,
  mailSenderChange,
  normalizeSlug,
  redirectMonthlyUsd,
  SLUG_RE,
  slugChangeAction,
  slugCheckPath,
  slugPath,
  tooSoonUntil,
  urlRows,
  type RedirectChoice,
  type SlugChangeResult,
  type SlugCheck,
  type SlugView
} from '../slugRename';
import type { useReauth } from './Reauth';

/**
 * メール配信を最初の公開で見せない間（2026-09-29 creator/mail-close・launchHidden.ts の mail）は、送信元が「変わる」行と
 * 配信停止のリンクの行を出さない。WHY: 送ったことの無いゲームには意味の無い話で、閉じた機能（利用者へのメール）の名前だけが見えてしまう。
 * 「元の名前のまま」（pinned）は出す = 閉じる前に実際に送ったゲームの事実で、そのゲームの利用者の手元には停止のリンクも残っている（停止の口は開けたまま）
 */
const MAIL_HIDDEN = isLaunchHidden('mail');

/** 月額の表示（$1 / $1.50）。整数なら小数を出さない */
export const usd = (n: number) => formatNumber(n, { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 });

export interface SlugDialogInitial {
  slug?: string;
  choice?: 'keep' | 'drop';
  /** 開いたらすぐ確かめる（undo・戻ってきたとき） */
  autoCheck?: boolean;
}

/** エラーの文言。変更の間隔の上限は次に変えられる日を入れる（errorText の差し込みには日付が無いため） */
function useSlugErrorText() {
  const { t } = useT();
  return (err: { code?: string; message: string; details?: Record<string, unknown> }, fallbackUntil?: string | null) => {
    const until = tooSoonUntil(err) ?? (err.code === 'slug_change_too_soon' ? (fallbackUntil ?? null) : null);
    // 閉鎖済み（409 account_closed）の共通の文言は「新しいゲームは作れません」= この画面向けに言い換える
    return until ? t('slug.tooSoon', { date: formatDateTime(until) }) : errorText(err, { account_closed: 'slug.accountClosed' });
  };
}

export function SlugChangeDialog({
  projectId,
  view,
  initial,
  reauth,
  onDone,
  onClose
}: {
  projectId: string;
  view: SlugView;
  initial: SlugDialogInitial;
  reauth: ReturnType<typeof useReauth>;
  onDone: (res: SlugChangeResult) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const errText = useSlugErrorText();
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [input, setInput] = useState(initial.slug ?? '');
  const [step, setStep] = useState<'input' | 'confirm'>('input');
  const [check, setCheck] = useState<SlugCheck | null>(null);
  const [choice, setChoice] = useState<RedirectChoice>(initial.choice ?? null);
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // 「変更する」を選ばずに押したとき（ボタンは押せないが、Enter で送った等）の案内
  const [choiceMissing, setChoiceMissing] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    d?.querySelector<HTMLInputElement>('input[name=slug]')?.focus();
  }, []);

  const runCheck = async (raw: string) => {
    const slug = normalizeSlug(raw);
    setInput(slug);
    setErr(null);
    setLocalErr(null);
    setSuggestions([]);
    if (!SLUG_RE.test(slug)) {
      setLocalErr(t('projects.form.slugInvalid'));
      return;
    }
    setBusy(true);
    try {
      const c = await api<SlugCheck>(slugCheckPath(projectId, slug));
      setCheck(c);
      if (c.available) {
        setStep('confirm');
      } else {
        setSuggestions(checkSuggestions(c));
        if (c.error) setLocalErr(errText(c.error, c.nextChangeAt ?? view.nextChangeAt));
      }
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  // undo・「Google で確認」から戻ったとき: 開いたらすぐ確かめる（1 回だけ）
  const autoRan = useRef(false);
  useEffect(() => {
    if (initial.autoCheck && initial.slug && !autoRan.current) {
      autoRan.current = true;
      void runCheck(initial.slug);
    }
    // 依存は空（開いたときの 1 回だけ。runCheck は毎回作り直されるので入れない）
  }, []);

  const onCheck = (e: FormEvent) => {
    e.preventDefault();
    if (!busy) void runCheck(input);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !check) return;
    if (!canConfirmChange(check, choice)) {
      setChoiceMissing(true);
      return;
    }
    const picked = choice as 'keep' | 'drop';
    setBusy(true);
    setErr(null);
    try {
      const res = await reauth.run(
        // 再認証は共通の形（400 reauthentication_required / 401 reauthentication_failed）= run がダイアログを出して送り直す
        (proof) => api<SlugChangeResult>(slugPath(projectId), { method: 'POST', body: changeBody(check.slug, picked, proof) }),
        { action: slugChangeAction(check.slug, picked) }
      );
      if (res) onDone(res);
    } catch (e2) {
      const ae = e2 as ApiError;
      // 確かめた後に他の人が取った・予約語になった・今のスラッグ = 入力に戻して候補を出す
      if (ae.code === 'slug_taken' || ae.code === 'slug_reserved' || ae.code === 'slug_unchanged' || ae.code === 'invalid_request') {
        setStep('input');
        setSuggestions(slugSuggestionsFrom(ae));
      }
      setErr(ae);
    } finally {
      setBusy(false);
    }
  };

  const price = redirectMonthlyUsd(check, view);
  const keepCost =
    price === null ? t('slug.choice.keepCostUnknown') : t(isDailyProration(check, view) ? 'slug.choice.keepCost' : 'slug.choice.keepCostMonthly', { price: usd(price) });
  const effects = check?.effects;
  const oldSlug = effects?.oldSlug ?? view.slug;
  const mail = mailSenderChange(check);
  // 回数の上限: 取り消し（undo）は数えない・数えない変更・この変更の後の次に変えられる日（案の check.nextChangeAt = 変えた後の日）
  const limitText = check?.isUndo
    ? t('slug.confirm.undo')
    : check && !check.countsTowardLimit
      ? t('slug.confirm.noCount')
      : check?.nextChangeAt
        ? t('slug.confirm.limit', { date: formatDateTime(check.nextChangeAt) })
        : null;
  // 独自ドメインは slug と無関係（サーバーは常に 'unchanged'）。持っていない人にも害の無い 1 行なので、そのまま出す
  const domainsUnchanged = effects?.customDomains === 'unchanged';

  return (
    <dialog ref={ref} className="dialog slug-dialog" aria-labelledby={`${id}-t`} onClose={onClose} onCancel={onClose}>
      {step === 'input' || !check ? (
        <form onSubmit={onCheck}>
          <h2 id={`${id}-t`}>{t('slug.dialog.title')}</h2>
          <div className="dialog-body">
            <label className="field">
              <span>{t('slug.dialog.newSlug')}</span>
              <input
                name="slug"
                value={input}
                onChange={(e) => setInput(e.target.value.toLowerCase())}
                placeholder={view.slug}
                autoComplete="off"
                spellCheck={false}
                maxLength={58}
                aria-describedby={`${id}-hint`}
              />
              <small id={`${id}-hint`} className="muted">
                {t('slug.dialog.hint')}
              </small>
            </label>
            {(localErr || err) && (
              <p className="alert alert-error" role="alert">
                {localErr ?? (err ? errText(err, view.nextChangeAt) : '')}
              </p>
            )}
            {suggestions.length > 0 && (
              <div className="slug-suggestions">
                <span className="small muted">{t('slug.dialog.suggestions')}</span>
                {suggestions.map((s) => (
                  <button key={s} type="button" className="btn btn-ghost btn-sm mono" disabled={busy} onClick={() => void runCheck(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || !input.trim()}>
              {busy ? t('common.working') : t('slug.dialog.check')}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={submit}>
          <h2 id={`${id}-t`}>{t(check.isUndo ? 'slug.confirm.undoTitle' : 'slug.confirm.title', { old: oldSlug, new: check.slug })}</h2>
          <div className="dialog-body">
            <UrlCompare before={urlRows(effects?.oldUrls)} after={urlRows(effects?.newUrls)} />
            {domainsUnchanged && <p className="small">{t('slug.confirm.customDomains')}</p>}

            <fieldset className="choice-group slug-choices">
              <legend>{t('slug.confirm.oldLinks', { old: oldSlug })}</legend>
              {/* 既定の選択なし（checked は本人が選んだときだけ）。並べて見せる = founder の決定 */}
              <label className={`choice ${choice === 'keep' ? 'is-active' : ''}`}>
                <input type="radio" name="redirect" value="keep" checked={choice === 'keep'} onChange={() => (setChoice('keep'), setChoiceMissing(false))} />
                <span>
                  <strong>{t('slug.choice.keep')}</strong>
                  <small>{keepCost}</small>
                </span>
              </label>
              <label className={`choice ${choice === 'drop' ? 'is-active' : ''}`}>
                <input type="radio" name="redirect" value="drop" checked={choice === 'drop'} onChange={() => (setChoice('drop'), setChoiceMissing(false))} />
                <span>
                  <strong>{t('slug.choice.drop')}</strong>
                  <small>{t('slug.choice.dropNote')}</small>
                </span>
              </label>
            </fieldset>
            {choice === null && (
              <p className={`small ${choiceMissing ? 'alert alert-error' : 'muted'}`} role={choiceMissing ? 'alert' : undefined}>
                {t('slug.choice.required')}
              </p>
            )}

            <ul className="small slug-notes">
              {limitText && <li>{limitText}</li>}
              {mail?.kind === 'pinned' && <li>{t('slug.confirm.mailPinned', { address: mail.address })}</li>}
              {mail?.kind === 'changes' && !MAIL_HIDDEN && <li>{t('slug.confirm.mailChanges', { before: mail.before, after: mail.after })}</li>}
              {effects?.unsubscribeLinks === 'keep_working' && (!MAIL_HIDDEN || mail?.kind === 'pinned') && <li>{t('slug.confirm.unsubscribe')}</li>}
              <li className="muted">{t('slug.confirm.reauth')}</li>
            </ul>

            {err && (
              <p className="alert alert-error" role="alert">
                {errText(err, check.nextChangeAt)}
              </p>
            )}
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => (setStep('input'), setErr(null))}>
              {t('slug.confirm.back')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || !canConfirmChange(check, choice)}>
              {busy ? t('common.working') : t('slug.confirm.submit')}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}

/** 新旧の URL を並べる（production・development の順。無い行は出さない） */
function UrlCompare({ before, after }: { before: Array<{ env: string; url: string }>; after: Array<{ env: string; url: string }> }) {
  const { t } = useT();
  if (!before.length && !after.length) return null;
  return (
    <dl className="slug-urls">
      <dt>{t('slug.confirm.urlsBefore')}</dt>
      <dd>
        {before.map((u) => (
          <span key={u.url} className="mono small slug-url-old">
            {u.url}
          </span>
        ))}
      </dd>
      <dt>{t('slug.confirm.urlsAfter')}</dt>
      <dd>
        {after.map((u) => (
          <span key={u.url} className="mono small">
            {u.url}
          </span>
        ))}
      </dd>
    </dl>
  );
}
